import React, { useEffect, useRef, useState } from 'react'
import { Loader2, Upload, Camera, X, Paperclip } from 'lucide-react'
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea
} from '../ui'
import SeccionesBuilder from './SeccionesBuilder'
import HabilidadesSelector from './HabilidadesSelector'
import ImportarPlantillasModal from './ImportarPlantillasModal'
import { LayoutGrid } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { useOrganization } from '../../contexts/OrganizationContext'
import { useConfirmDialog } from '../../hooks/useConfirmDialog'
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard'
import { useDeportes } from '../../hooks/useDeportes'
import { opcionesDeporte, SIN_DEPORTES_MSG } from '../../lib/deportesOrg'
import {
  attachProgramFiles,
  deleteProgramFile,
  getOrgDeportes,
  saveProgramaCompleto,
  getProgramaWithSeccionesYHabilidades,
  uploadTrainingProgramAsset
} from '../../lib/supabase'
import { supabase } from '../../lib/supabaseClient'

import {
  normalizarDeporte,
  buildProgramaPayload
} from './programaEditorHelpers'

const ProgramaEditorModal = ({
  open,
  onOpenChange,
  programaId = null,
  onSaved
}) => {
  const { staff } = useAuth()
  const { org } = useOrganization()
  const guard = useUnsavedGuard({ onClose: () => onOpenChange(false) })
  const isEditing = Boolean(programaId)
  const [tab, setTab] = useState('info')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const [titulo, setTitulo] = useState('')
  const [descripcion, setDescripcion] = useState('')
  const [deportes, setDeportes] = useState([])
  const { label: nombreDeporte } = useDeportes()
  const [habilidadIds, setHabilidadIds] = useState([])
  const [orgDeportes, setOrgDeportes] = useState([])

  const [secciones, setSecciones] = useState([])

  const [importarOpen, setImportarOpen] = useState(false)
  const [newFiles, setNewFiles] = useState([])
  const [existingFiles, setExistingFiles] = useState([])
  const fileInputRef = useRef(null)
  const cameraInputRef = useRef(null)

  const { confirm, ConfirmDialog } = useConfirmDialog()

  // Cargar deportes habilitados de la organizacion una sola vez al abrir
  useEffect(() => {
    if (!open || !org?.id) return
    let cancelled = false
    getOrgDeportes(org.id).then(({ data }) => {
      if (!cancelled) setOrgDeportes(data || [])
    })
    return () => { cancelled = true }
  }, [open, org?.id])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!open) return
      if (!programaId) {
        setTitulo('')
        setDescripcion('')
        setDeportes([])
        setHabilidadIds([])
        setSecciones([])
        setNewFiles([])
        setExistingFiles([])
        setError(null)
        setTab('info')
        guard.resetDirty()
        return
      }
      setLoading(true)
      setError(null)
      const { data, error: err } = await getProgramaWithSeccionesYHabilidades(programaId)
      if (cancelled) return
      if (err) {
        setError('No pudimos cargar el programa')
      } else {
        setTitulo(data.titulo || '')
        setDescripcion(data.descripcion || '')
        // Normalizar deportes guardados (compat con datos viejos en formato label)
        const { data: orgDep } = await getOrgDeportes(org.id)
        const orgDepList = orgDep || []
        setOrgDeportes(orgDepList)
        const deportesNormalizados = (data.deportes || [])
          .map((d) => normalizarDeporte(d, orgDepList))
          .filter(Boolean)
        setDeportes(deportesNormalizados)
        setHabilidadIds((data.habilidades || []).map((h) => h.id))
        setSecciones((data.secciones || []).map((s) => ({
          nombre: s.nombre,
          duracion_seg: s.duracion_seg,
          descripcion: s.descripcion || '',
          tipo: s.tipo,
          habilidad_ids: s.habilidad_ids || []
        })))
        setExistingFiles(data.archivos || [])
        guard.resetDirty()
      }
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [open, programaId]) // eslint-disable-line react-hooks/exhaustive-deps

  const chipsDeporte = opcionesDeporte(orgDeportes, deportes, nombreDeporte)

  const toggleDeporte = (deporteId) => {
    guard.markDirty()
    if (deportes.includes(deporteId)) {
      setDeportes(deportes.filter((d) => d !== deporteId))
    } else {
      setDeportes([...deportes, deporteId])
    }
  }

  const handleFileSelect = (event) => {
    const files = event.target.files
    if (files?.length) {
      guard.markDirty()
      setNewFiles((prev) => [...prev, ...Array.from(files)])
    }
    event.target.value = ''
  }

  const removeNewFile = (file) => {
    guard.markDirty()
    setNewFiles((prev) => prev.filter((f) => f !== file))
  }

  const handleRemoveExistingFile = async (fileId) => {
    const { error: err } = await deleteProgramFile(fileId)
    if (err) {
      setError('No pudimos eliminar el archivo')
      return
    }
    setExistingFiles((prev) => prev.filter((f) => f.id !== fileId))
  }

  const handleGuardar = async () => {
    // Validacion + construccion del payload con orgId del CONTEXTO
    // (no de staff.org_id — eso era el bug original con impersonation).
    const { error: validationError, payload } = buildProgramaPayload({
      titulo,
      descripcion,
      deportes,
      habilidadIds,
      secciones,
      staffId: staff?.id,
      orgId: org?.id
    })
    if (validationError) {
      setError(validationError)
      return
    }

    // Aviso si hay asignaciones futuras (solo en modo edicion)
    if (isEditing) {
      const { data: asignacionesFuturas } = await supabase
        .from('programas_entrenamiento_agenda')
        .select('id, fecha, grupo:grupos(nombre_del_grupo)')
        .eq('programa_id', programaId)
        .eq('activo', true)
        .gte('fecha', new Date().toISOString().split('T')[0])
        .limit(5)

      if (asignacionesFuturas && asignacionesFuturas.length > 0) {
        const fechas = asignacionesFuturas.map((a) => a.fecha).slice(0, 3).join(', ')
        const ok = await confirm(
          'Programa con asignaciones futuras',
          `Este programa esta asignado a ${asignacionesFuturas.length} sesion(es) futura(s) (${fechas}${asignacionesFuturas.length > 3 ? '...' : ''}). Tus cambios se reflejaran al iniciarlas. ¿Continuar con el guardado?`,
          { confirmText: 'Guardar de todos modos' }
        )
        if (!ok) return
      }
    }

    setSaving(true)
    setError(null)
    try {
      // saveProgramaCompleto espera el shape de su API; separamos del payload helper
      const { habilidadIds: habIds, secciones: secs, ...programaSinExtras } = payload
      const { data: savedPrograma, error: errSave } = await saveProgramaCompleto({
        programaId,
        programa: programaSinExtras,
        secciones: secs,
        habilidadIds: habIds
      })
      if (errSave) throw errSave

      if (newFiles.length) {
        const uploaded = []
        for (const f of newFiles) {
          const { data: up, error: upErr } = await uploadTrainingProgramAsset(f, savedPrograma.id)
          if (upErr) throw upErr
          uploaded.push(up)
        }
        if (uploaded.length) {
          const { error: attachErr } = await attachProgramFiles(savedPrograma.id, uploaded)
          if (attachErr) throw attachErr
        }
      }

      guard.resetDirty()
      onOpenChange(false)
      if (onSaved) onSaved(savedPrograma)
    } catch (err) {
      console.error(err)
      setError(err.message || 'No pudimos guardar el programa')
    } finally {
      setSaving(false)
    }
  }

  // deportes ya es array de deporte_id (slugs)
  const duracionTotalMin = Math.floor(
    secciones.reduce((s, x) => s + x.duracion_seg, 0) / 60
  )

  return (
    <>
      <Dialog open={open} onOpenChange={guard.onOpenChange}>
        <DialogContent {...guard.dirtyProps} className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {isEditing ? 'Editar programa de entrenamiento' : 'Nuevo programa de entrenamiento'}
            </DialogTitle>
            <DialogDescription>
              Construye la rutina paso a paso. Las secciones se pueden cronometrar en cancha durante el entrenamiento.
            </DialogDescription>
          </DialogHeader>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Cargando programa...
            </div>
          ) : (
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="info">Información</TabsTrigger>
                <TabsTrigger value="secciones">Secciones</TabsTrigger>
                <TabsTrigger value="material">Material</TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="titulo">Titulo</Label>
                  <Input
                    id="titulo"
                    value={titulo}
                    onChange={(e) => setTitulo(e.target.value)}
                    placeholder="Ej: Flag Ofensiva"
                  />
                </div>
                <div>
                  <Label htmlFor="descripcion">Descripción general</Label>
                  <Textarea
                    id="descripcion"
                    rows={3}
                    value={descripcion}
                    onChange={(e) => setDescripcion(e.target.value)}
                    placeholder="Objetivo del programa, notas generales..."
                  />
                </div>
                <div>
                  <Label>Deportes</Label>
                  {orgDeportes.length === 0 && (
                    <p className="mt-2 text-xs text-muted-foreground">{SIN_DEPORTES_MSG}</p>
                  )}
                  {/* Un deporte ya deshabilitado que el programa traia sale marcado:
                      se ve y se puede quitar, pero no volver a poner. */}
                  {chipsDeporte.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {chipsDeporte.map((o) => {
                        const id = o.value
                        const label = o.label
                        const isSelected = deportes.includes(id)
                        return (
                          <button
                            key={id}
                            type="button"
                            onClick={() => toggleDeporte(id)}
                            className={`rounded-full border px-3 py-1 text-xs ${
                              isSelected
                                ? 'border-primary bg-accent text-accent-foreground'
                                : 'border-input text-muted-foreground hover:bg-muted/50'
                            }`}
                          >
                            {label}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
                <HabilidadesSelector
                  deportes={deportes}
                  selectedIds={habilidadIds}
                  onChange={(ids) => { guard.markDirty(); setHabilidadIds(ids) }}
                />
                <p className="text-xs text-muted-foreground">
                  Qué habilidades desarrolla este programa (del catálogo de tu deporte).
                  También puedes marcar habilidades en cada sección.
                </p>
              </TabsContent>

              <TabsContent value="secciones" className="mt-4 space-y-3">
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setImportarOpen(true)}
                  >
                    <LayoutGrid className="h-4 w-4 mr-1" />
                    Importar de plantillas
                  </Button>
                </div>
                <SeccionesBuilder
                  secciones={secciones}
                  onChange={(next) => { guard.markDirty(); setSecciones(next) }}
                  deportes={deportes}
                />
              </TabsContent>

              <TabsContent value="material" className="mt-4 space-y-3">
                <p className="text-sm text-muted-foreground">
                  Sube archivos de referencia (PDF, imagen, diagrama). Estaran disponibles a los coaches durante el entrenamiento.
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                    <Upload className="h-4 w-4 mr-1" />
                    Seleccionar archivo
                  </Button>
                  <Button variant="outline" onClick={() => cameraInputRef.current?.click()}>
                    <Camera className="h-4 w-4 mr-1" />
                    Tomar foto
                  </Button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="application/pdf,image/*"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleFileSelect}
                />

                {newFiles.length > 0 && (
                  <div className="space-y-1 rounded-md border border-border p-3">
                    <p className="text-xs text-muted-foreground">Nuevos archivos</p>
                    {newFiles.map((f) => (
                      <div key={`${f.name}-${f.size}`} className="flex items-center justify-between text-sm">
                        <span className="truncate">{f.name}</span>
                        <Button size="sm" variant="ghost" onClick={() => removeNewFile(f)}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {existingFiles.length > 0 && (
                  <div className="space-y-1 rounded-md border border-border p-3">
                    <p className="text-xs text-muted-foreground">Archivos actuales</p>
                    {existingFiles.map((f) => (
                      <div key={f.id} className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-1 truncate">
                          <Paperclip className="h-3.5 w-3.5" />
                          {f.file_name}
                        </span>
                        <Button size="sm" variant="ghost" onClick={() => handleRemoveExistingFile(f.id)}>
                          Quitar
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter className="flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-xs text-muted-foreground">
              {secciones.length > 0 && (
                <>
                  {secciones.length} secciones · {duracionTotalMin} min totales
                </>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => guard.onOpenChange(false)}>
                Cancelar
              </Button>
              <Button onClick={handleGuardar} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                {isEditing ? 'Guardar cambios' : 'Guardar programa'}
              </Button>
            </div>
          </DialogFooter>
          {guard.DiscardDialog}
        </DialogContent>
      </Dialog>
      <ImportarPlantillasModal
        open={importarOpen}
        onOpenChange={setImportarOpen}
        deportes={deportes}
        onImport={(nuevas) => { guard.markDirty(); setSecciones((prev) => [...prev, ...nuevas]) }}
      />
      <ConfirmDialog />
    </>
  )
}

export default ProgramaEditorModal
